/**
 * Phone "Log application" for one pond's open crop (active or preparing):
 * product tiles (recent first), a "why" chip, the amount on the in-app keypad
 * (½ + Save), and an optional note. One product per entry (D6: a 10-10-10 mix
 * is three entries). A failed save keeps everything typed for "Try again".
 */
import { DateBar } from '@/components/entry/DateBar';
import { BundleApply } from '@/components/entry/BundleApply';
import { useApplicationBundles } from '@/hooks/useApplicationBundles';
import { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronLeft, Search, X } from 'lucide-react';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/apiErrors';
import { cn } from '@/lib/cn';
import { qk } from '@/lib/queryKeys';
import { displayPhDate, todayInPH } from '@/lib/phDate';
import { cropPhase, docOn, prepDayOn, type Crop } from '@/lib/crop';
import { Keypad } from '@/components/entry/Keypad';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { amountText, categoryLabel } from '@/components/pond/page/format';
import { useApplicationJournal, useFarmProducts, useReadingReasons } from '@/components/pond/page/data';
import type { FarmProductResponse } from '@/types/generated/openapi';
import { addPhDays } from '@/lib/phDate';
import { usePondCrop } from './usePondCrop';
import { PhoneMenuButton } from '@/components/layout/PhoneMenuButton';
import { bigAmountLimit, fitsPrecision, PRECISION_HINT } from '@/lib/applicationAmount';
import { canEditApplication, wasEdited } from '@/lib/applicationEdit';
import { useIdempotencyKey } from '@/lib/idempotency';
import { useAuth } from '@/hooks/useAuth';
import { useMembership } from '@/hooks/useMembership';
import { PhoneApplicationEdit } from './PhoneApplicationEdit';
import type { ApplicationResponse } from '@/types/generated/openapi';

const TILE_COUNT = 3;
const RECENT_COUNT = 8;
const ROUTINE = 'Routine';
const OTHER = 'other';

interface Props {
  farmId: string | undefined;
  pondId: string;
  /** The `date` search param: a past day being back-filled (#366). Unset = today. */
  date?: string;
}

export function PhoneApplication({ farmId, pondId, date }: Props) {
  const { data: pond, isLoading, isError, refetch } = usePondCrop(farmId, pondId);
  const crop = pond?.selected_cycle && cropPhase(pond.selected_cycle) !== 'closed' ? pond.selected_cycle : null;

  if (isLoading || !farmId) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">
        <p>Couldn't load this pond.</p>
        <button type="button" onClick={() => refetch()} className="mt-3 min-h-11 rounded-lg px-4 font-semibold text-primary-600">
          Try again
        </button>
      </div>
    );
  }
  if (!pond || !crop) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
        <p>This pond has no open crop to log an application on.</p>
        <Link to="/entry" className="mt-3 inline-flex min-h-11 items-center font-semibold text-primary-600">
          Back to Today
        </Link>
      </div>
    );
  }
  // Keyed by the day too: reason chips are specific to a day's readings, and a pick made on another day
  // would be hidden but still sent.
  return <ApplicationScreen key={`${crop.id}|${date ?? ''}`} farmId={farmId} pondId={pondId} pondName={pond.name} crop={crop} entryDate={date} areaHa={pond.area_ha} />;
}

function dayLabel(crop: Crop, date: string): string | null {
  const doc = docOn(crop, date);
  if (doc !== null) return `DOC ${doc}`;
  const prep = prepDayOn(crop, date);
  return prep !== null ? `Prep day ${prep}` : null;
}

function ApplicationScreen({
  farmId,
  pondId,
  pondName,
  crop,
  entryDate,
  areaHa,
}: {
  farmId: string;
  pondId: string;
  pondName: string;
  crop: Crop;
  entryDate?: string;
  areaHa?: number | string | null;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const bundles = useApplicationBundles(farmId);
  const [bundleOpen, setBundleOpen] = useState(false);
  const realToday = todayInPH();
  // The day this entry is for: today, or a past day being back-filled (never the future).
  const today = entryDate && entryDate < realToday ? entryDate : realToday;
  const cropStart = crop.prep_start_date ?? crop.stocking_date ?? null;
  const preparing = cropPhase(crop) === 'prep';
  const { data: products = [], isLoading: productsLoading } = useFarmProducts(farmId);
  const readingReasons = useReadingReasons(farmId, crop.id, today);

  const [productId, setProductId] = useState<string | null>(null);
  const [reasonPick, setReasonPick] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [amount, setAmount] = useState('');
  const [hint, setHint] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [textFocus, setTextFocus] = useState(false);
  const [logged, setLogged] = useState<string | null>(null);
  const journal = useApplicationJournal(farmId, crop.id, {});
  const amountRef = useRef<HTMLDivElement>(null);
  const idempotency = useIdempotencyKey();
  const { user } = useAuth();
  const { membership, isSupervisorOrAbove } = useMembership();
  const [editing, setEditing] = useState<ApplicationResponse | null>(null);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentShown, setRecentShown] = useState(RECENT_COUNT);
  const allRecent = journal.data?.pages.flatMap((pg) => pg.data) ?? [];
  const recentApps = allRecent.slice(0, recentShown);
  const moreRecent = allRecent.length > recentShown || !!journal.hasNextPage;
  const showMoreRecent = () => {
    setRecentShown((n) => n + RECENT_COUNT);
    if (allRecent.length < recentShown + RECENT_COUNT && journal.hasNextPage) journal.fetchNextPage();
  };
  const canEdit = (row: ApplicationResponse) =>
    canEditApplication(
      { isSupervisorOrAbove, membersEdit: !!membership?.membersEditApplications, cropOpen: true, userId: user?.id },
      row
    );
  // Only offer the list when there is something in it to fix; rows that can't be edited do nothing.
  const anyEditable = recentApps.some(canEdit);

  const active = products.filter((p) => p.is_active);
  const product = active.find((p) => p.id === productId) ?? null;
  // The server orders by last use: the caption only means something once something has been used.
  const hasHistory = active.some((p) => p.last_used_date);
  const recent = active.slice(0, TILE_COUNT);
  const tiles = product && !recent.includes(product) ? [...recent.slice(0, TILE_COUNT - 1), product] : recent;
  const reason = reasonPick === OTHER ? reasonText.trim() : reasonPick;
  const amountNum = amount === '' || amount === '.' ? NaN : Number(amount);

  const saveKey = ['phone-application', crop.id] as const;
  // An earlier submit that is still queued (offline) survives leaving and re-opening
  // this screen: count it so the same application can't be logged twice.
  const queuedSaves = useMutationState({
    filters: { mutationKey: saveKey, status: 'pending' },
    select: (m) => m.state.isPaused,
  });
  const queued = queuedSaves.length > 0;
  const waitingForConnection = queuedSaves.some(Boolean);

  const save = useMutation({
    mutationKey: saveKey,
    mutationFn: (v: { body: Record<string, string | number | null>; label: string }) =>
      api.post(`/api/v1/farms/${farmId}/applications`, v.body),
    // POST isn't idempotent: a silent retry after a lost response would log it twice.
    // The person retries with "Try again" instead.
    retry: false,
    onSuccess: (_r, { label }) => {
      idempotency.reset();
      toast.success(`Logged ${label}`);
      setLogged(label);
      queryClient.invalidateQueries({ queryKey: qk.applications.crop(farmId, crop.id) });
      queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    },
    // Mutation-level so it still fires when a queued save resumes after this screen closed.
    onError: () => {
      toast.error(`Application for ${pondName} wasn't saved. Log it again.`);
    },
  });

  // A queued save lives in memory only: reloading or closing the tab drops it.
  useEffect(() => {
    if (!queued) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [queued]);

  // A half-entered new application is in memory only; once Save is tapped the mutation owns it.
  const unsavedEntry = !logged && !save.isPending && !queued && (productId !== null || amount !== '');
  useBlocker({
    shouldBlockFn: () => !window.confirm('Not saved yet. Leave anyway?'),
    disabled: !unsavedEntry,
    enableBeforeUnload: unsavedEntry,
  });

  useEffect(() => {
    amountRef.current?.scrollIntoView({ block: 'nearest' });
  }, [amount]);

  // Soft confirm for a likely typo (9,999,999 kg): more than 3x the largest earlier entry of this
  // product on this crop, or with no history more than 1,000 kg/L, 1,000,000 g/mL or 100,000 pcs.
  const priorMax = (journal.data?.pages ?? [])
    .flatMap((pg) => pg.data ?? [])
    .filter((a) => product && a.product_id === product.id)
    .reduce((m, a) => Math.max(m, Number(a.amount) || 0), 0);
  const [bigOk, setBigOk] = useState<number | null>(null);

  const submit = () => {
    if (save.isPending || queued) return;
    if (!product) {
      setHint('Pick a product first');
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setHint('Type the amount');
      return;
    }
    const limit = bigAmountLimit(priorMax, product.unit);
    if (amountNum > limit && bigOk !== amountNum) {
      setBigOk(amountNum);
      setHint(`${amountText(amountNum, product.unit)} is a lot. Tap Save again if it is right.`);
      return;
    }
    setHint('');
    save.mutate({
      body: {
        cycle_id: crop.id,
        product_id: product.id,
        amount: amountNum,
        application_date: today,
        reason: reason || null,
        notes: notes.trim() || null,
        idempotency_key: idempotency.keyFor(
          JSON.stringify([crop.id, product.id, amountNum, today, reason, notes.trim()])
        ),
      },
      label: `${product.name} ${amountText(amountNum, product.unit)}`,
    });
  };

  const logAnother = () => {
    save.reset();
    setLogged(null);
    setProductId(null);
    setReasonPick('');
    setReasonText('');
    setNotes('');
    setNoteOpen(false);
    setAmount('');
  };

  const onDigit = (c: string) => {
    if (save.isPending) return;
    let next: string;
    if (c === '.') {
      if (amount.includes('.')) return;
      next = `${amount}.`;
    } else {
      next = amount === '0' ? c : amount + c;
    }
    if (!fitsPrecision(next)) {
      setHint(PRECISION_HINT);
      return;
    }
    setHint('');
    setAmount(next);
  };
  // "2½" means 2.5, so ½ sets the fraction to .5 (2.75 → 2.5) rather than adding 0.5.
  const onHalf = () => {
    if (save.isPending) return;
    setHint('');
    setAmount(`${amount.split('.')[0] || '0'}.5`);
  };

  const pickProduct = (p: FarmProductResponse) => {
    if (save.isPending) return;
    setProductId(p.id);
    setHint('');
    setSearchOpen(false);
  };
  const toggleReason = (r: string) => setReasonPick((cur) => (cur === r ? '' : r));

  const back = preparing
    ? ({ to: '/entry/pond/$pondId', params: { pondId }, label: 'Back to prep' } as const)
    : ({ to: '/entry', params: {}, label: 'Back to Today' } as const);
  const day = dayLabel(crop, today);
  const unit = product?.unit ?? '';
  const showKeypad = !logged && !textFocus && !searchOpen;
  const amountShown = amount === '' ? '' : amount.startsWith('.') ? `0${amount}` : amount;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100" data-phone-apply>
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-4 pb-3 pt-2">
        <Link
          to={back.to}
          params={back.params}
          search={{ date: today === realToday ? undefined : today }}
          aria-label={back.label}
          className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900 active:bg-gray-100"
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[17px] font-bold text-gray-900">{pondName} · Log application</h1>
          <p className="text-xs text-gray-500">{today === realToday ? (day ?? 'Today') : `${day ? `${day} · ` : ''}Entering for ${displayPhDate(today, { month: 'short', day: 'numeric' })}`}</p>
        </div>
        <PhoneMenuButton />
      </header>

      {!logged && (
        <div className="shrink-0 border-b border-gray-200 bg-white px-4 pb-2.5 pt-2">
          <DateBar
            date={today}
            today={realToday}
            minDate={cropStart}
            disabled={save.isPending || queued}
            onChange={(d) => navigate({ to: '/entry/pond/$pondId/application', params: { pondId }, search: { date: d === realToday ? undefined : d } })}
          />
        </div>
      )}

      {logged ? (
        <div className="flex-1 space-y-3 px-4 py-6" data-apply-done>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center">
            <p className="text-sm text-emerald-800">Saved</p>
            <p className="mt-1 text-lg font-bold text-gray-900">{logged}</p>
          </div>
          <button
            type="button"
            onClick={logAnother}
            className="flex min-h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-[15px] font-semibold text-white"
          >
            Log another
          </button>
          <Link
            to={back.to}
            params={back.params}
            search={{ date: today === realToday ? undefined : today }}
            className="flex min-h-12 w-full items-center justify-center rounded-xl border border-gray-300 bg-white text-[15px] font-semibold text-gray-900"
          >
            {back.label}
          </Link>
          {anyEditable && (
            <RecentApplications rows={recentApps} today={today} canEdit={canEdit} onEdit={setEditing} onMore={moreRecent ? showMoreRecent : undefined} />
          )}
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-3" data-apply-body>
          {bundles.length > 0 && (
            <button
              type="button"
              onClick={() => setBundleOpen(true)}
              className="flex min-h-12 w-full items-center justify-between rounded-xl border border-primary-300 bg-primary-50 px-3 text-left text-[15px] font-semibold text-primary-800 touch-manipulation"
              data-bundle-open
            >
              <span>Apply a bundle</span>
              <span className="text-sm font-medium">{bundles.map((b) => b.name).join(' · ')}</span>
            </button>
          )}
          {anyEditable && (
            <div>
              <button
                type="button"
                aria-expanded={recentOpen}
                onClick={() => setRecentOpen((o) => !o)}
                className="flex min-h-11 w-full items-center justify-between rounded-xl border border-gray-300 bg-white px-3 text-left text-[15px] font-semibold text-gray-900 touch-manipulation"
                data-recent-toggle
              >
                <span>Fix a logged application</span>
                <span className="text-sm font-medium text-primary-600">{recentOpen ? 'Hide' : `${recentApps.length} recent`}</span>
              </button>
              {recentOpen && (
                <RecentApplications rows={recentApps} today={today} canEdit={canEdit} onEdit={setEditing} bare onMore={moreRecent ? showMoreRecent : undefined} />
              )}
            </div>
          )}
          <fieldset className="space-y-1.5">
            <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{hasHistory ? 'Product · recent first' : 'Product'}</legend>
            {productsLoading ? (
              <LoadingSpinner />
            ) : active.length === 0 ? (
              <p className="text-sm text-gray-500">No products set up for this farm yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {tiles.map((p) => (
                  <ProductTile key={p.id} product={p} chosen={p.id === productId} onPick={() => pickProduct(p)} />
                ))}
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="flex min-h-[60px] flex-col items-start justify-center rounded-xl border border-dashed border-gray-300 bg-white px-3 text-left touch-manipulation"
                >
                  <span className="flex items-center gap-1.5 text-[15px] font-semibold text-gray-900">
                    <Search className="h-4 w-4" aria-hidden="true" /> Search all…
                  </span>
                  <span className="text-xs text-gray-500">{active.length} products</span>
                </button>
              </div>
            )}
          </fieldset>

          <fieldset className="space-y-1.5">
            <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Why this application · optional</legend>
            <div className="flex flex-wrap gap-2">
              {[...readingReasons, ROUTINE].map((r) => (
                <ReasonChip key={r} label={r} chosen={reasonPick === r} onPick={() => toggleReason(r)} />
              ))}
              <ReasonChip label="Other" chosen={reasonPick === OTHER} onPick={() => toggleReason(OTHER)} />
            </div>
            {reasonPick === OTHER && (
              <input
                type="text"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                onFocus={() => setTextFocus(true)}
                onBlur={() => setTextFocus(false)}
                placeholder="Why this application?"
                aria-label="Other reason"
                className="mt-2 min-h-11 w-full rounded-lg border border-gray-300 px-3 text-base"
              />
            )}
          </fieldset>

          <div
            ref={amountRef}
            className="scroll-my-3 flex items-center justify-between rounded-xl border-2 border-primary-600 bg-white px-3.5 py-3"
            data-amount
          >
            <span className="text-[15px] text-gray-900">Amount</span>
            <span className="flex items-baseline text-[28px] font-bold tabular-nums text-gray-900" data-amount-value>
              {amountShown === '' ? <span className="text-gray-400">—</span> : amountShown}
              {unit && <span className="ml-1 text-base font-medium text-gray-500">{unit}</span>}
              <span className="ml-0.5 inline-block h-6 w-0.5 animate-pulse self-center bg-primary-600" aria-hidden="true" />
            </span>
          </div>

          {hint && (
            <p className="px-1 text-sm font-medium text-amber-800" role="alert">
              {hint}
            </p>
          )}
          {waitingForConnection && (
            <p className="rounded-lg bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-900" role="status" data-apply-offline>
              No connection. Your entries for this pond will save when you are back online. Do not log it again.
            </p>
          )}
          {save.isError && save.error instanceof ApiError && save.error.status >= 400 && save.error.status < 500 ? (
            <p className="rounded-lg bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-800" role="alert" data-apply-refused>
              Not saved: {save.error.message}
            </p>
          ) : save.isError && (
            <button
              type="button"
              onClick={submit}
              className="flex min-h-11 w-full items-center justify-center rounded-lg bg-red-50 px-3 text-sm font-semibold text-red-800"
              data-apply-retry
            >
              Not saved · Try again
            </button>
          )}

          {noteOpen ? (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onFocus={() => setTextFocus(true)}
              onBlur={() => setTextFocus(false)}
              rows={3}
              placeholder="Note (optional)"
              aria-label="Note"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base"
            />
          ) : (
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="flex min-h-11 items-center px-1 text-[13px] font-semibold text-primary-600"
            >
              + Add a note
            </button>
          )}
        </div>
      )}

      {showKeypad && (
        <Keypad
          className="shrink-0"
          onDigit={onDigit}
          onDelete={() => setAmount((a) => a.slice(0, -1))}
          onClear={() => setAmount('')}
          digitsDisabled={save.isPending}
          secondaryKey={{ label: '½', ariaLabel: 'Half (set to point 5)', onPress: onHalf }}
          actionKey={{
            label: waitingForConnection ? 'Waiting…' : save.isPending ? 'Saving…' : 'Save',
            onPress: submit,
            disabled: save.isPending || queued,
          }}
          announcement={`${hint ? `${hint}. ` : ''}Amount: ${amountShown === '' ? 'blank' : `${amountShown} ${unit}`}`}
        />
      )}

      {!logged && textFocus && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-2">
          <button
            type="button"
            // Keep focus until the click lands; a touch on the button would otherwise blur
            // the field first, hide this bar, and swallow the tap.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (document.activeElement as HTMLElement | null)?.blur()}
            className="flex min-h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-[15px] font-semibold text-white"
          >
            Done
          </button>
        </div>
      )}

      {searchOpen && (
        <ProductSearch products={active} chosenId={productId} onPick={pickProduct} onClose={() => setSearchOpen(false)} />
      )}

      {bundleOpen && (
        <div className="fixed inset-0 z-[55] flex flex-col bg-gray-100" role="dialog" aria-modal="true" aria-label="Apply a bundle" data-bundle-sheet>
          <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-4 pb-3 pt-2">
            <button
              type="button"
              onClick={() => setBundleOpen(false)}
              aria-label="Close"
              className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900 active:bg-gray-100"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="truncate text-[17px] font-bold text-gray-900">{pondName} · Apply a bundle</h1>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <BundleApply
              farmId={farmId}
              cropId={crop.id}
              date={today}
              areaHa={areaHa}
              phone
              onSaved={(name) => {
                setBundleOpen(false);
                setLogged(name);
              }}
            />
          </div>
        </div>
      )}

      {editing && (
        <PhoneApplicationEdit
          key={editing.id}
          farmId={farmId}
          cropId={crop.id}
          row={editing}
          canDelete={isSupervisorOrAbove}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function dayName(date: string, today: string): string {
  if (date === today) return 'Today';
  if (date === addPhDays(today, -1)) return 'Yesterday';
  return format(parseISO(date), 'MMM d');
}

function RecentApplications({
  rows,
  today,
  canEdit,
  onEdit,
  bare,
  onMore,
}: {
  rows: ApplicationResponse[];
  today: string;
  canEdit: (row: ApplicationResponse) => boolean;
  onEdit: (row: ApplicationResponse) => void;
  bare?: boolean;
  onMore?: () => void;
}) {
  if (rows.length === 0) return null;
  return (
    <section className={cn('space-y-1.5', !bare && 'pt-2')} data-recent-applications>
      {!bare && <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recent applications · tap one to fix it</h2>}
      <ul className={cn('divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white', bare && 'mt-2')}>
        {rows.map((r) => {
          const body = (
            <>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[15px] font-semibold text-gray-900">{r.product_name || 'Unnamed'}</span>
                  {r.bundle_name && (
                    <span className="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700">{r.bundle_name}</span>
                  )}
                  {wasEdited(r) && (
                    <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">Edited</span>
                  )}
                </span>
                <span className="block text-xs text-gray-500">{dayName(r.application_date, today)}</span>
              </span>
              <span className="shrink-0 text-[15px] font-medium tabular-nums text-gray-900">{amountText(r.amount, r.unit)}</span>
            </>
          );
          return (
            <li key={r.id}>
              {canEdit(r) ? (
                <button
                  type="button"
                  onClick={() => onEdit(r)}
                  className="flex min-h-12 w-full items-center gap-3 px-3 py-2 text-left touch-manipulation active:bg-gray-50"
                  data-recent-row
                >
                  {body}
                </button>
              ) : (
                <div className="flex min-h-12 items-center gap-3 px-3 py-2">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
      {onMore && (
        <button type="button" onClick={onMore} className="flex min-h-11 w-full items-center justify-center text-sm font-semibold text-primary-600" data-recent-more>
          Show older
        </button>
      )}
    </section>
  );
}

function ProductTile({ product, chosen, onPick }: { product: FarmProductResponse; chosen: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={onPick}
      className={cn(
        'flex min-h-[60px] min-w-0 flex-col items-start justify-center rounded-xl border px-3 text-left touch-manipulation',
        chosen ? 'border-2 border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
      )}
    >
      <span className="w-full truncate text-[15px] font-semibold text-gray-900">{product.name}</span>
      <span className="w-full truncate text-xs text-gray-500">
        {categoryLabel(product.category)} · {product.unit}
      </span>
    </button>
  );
}

function ReasonChip({ label, chosen, onPick }: { label: string; chosen: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={onPick}
      className={cn(
        'min-h-11 min-w-11 max-w-full rounded-full border px-4 text-sm font-medium touch-manipulation',
        chosen ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700'
      )}
    >
      {label}
    </button>
  );
}

function ProductSearch({
  products,
  chosenId,
  onPick,
  onClose,
}: {
  products: FarmProductResponse[];
  chosenId: string | null;
  onPick: (p: FarmProductResponse) => void;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      trigger?.focus();
    };
  }, []);
  const q = filter.trim().toLowerCase();
  const shown = q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;
  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-0 z-[60] flex flex-col bg-white outline-none"
      role="dialog"
      aria-modal="true"
      aria-label="Choose a product"
      data-product-search
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-4 pb-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter products"
          aria-label="Filter products"
          className="min-h-11 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-base"
        />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
        {shown.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              aria-pressed={p.id === chosenId}
              onClick={() => onPick(p)}
              className={cn(
                'flex min-h-12 w-full flex-col items-start justify-center border-b border-gray-100 py-2 text-left',
                p.id === chosenId && 'font-semibold text-primary-700'
              )}
            >
              <span className="text-[15px] text-gray-900">{p.name}</span>
              <span className="text-xs text-gray-500">
                {categoryLabel(p.category)} · {p.unit}
              </span>
            </button>
          </li>
        ))}
        {shown.length === 0 && <li className="py-6 text-center text-sm text-gray-500">No product matches “{filter}”.</li>}
      </ul>
    </div>
  );
}
